import { Controller } from "@hotwired/stimulus"

// Einstellungsseite des Config Tools. Die Werte liegen in der Electron-Shell (Main-Prozess, settings.json), nicht auf
// dem Server: dieser Controller liest und schreibt sie nur über die Bridge window.configTool aus dem Preload-Skript
// der Shell. Ausserhalb der Shell fehlt die Bridge: Felder deaktiviert, Hinweis sichtbar.
//
// Ein `field`-Target pro Einstellung, Schlüssel in data-setting="<key>". Speichern schickt alle Felder als ein Patch;
// Durchsuchen öffnet den nativen Ordnerdialog der Shell und füllt das Feld aus dem Action-Param
// (data-config-tool--settings-setting-param). Der serielle Port ist ein Select (target "port", zugleich ein `field`)
// mit den Ports, die die Shell gerade sieht; ein gespeicherter, aber nicht angeschlossener Port bleibt als Option
// stehen. Texte kommen als Values aus der View.
export default class extends Controller {
  static targets = ["field", "port", "status", "unavailable"]
  static values = { saved: String, saveFailed: String, loadFailed: String, noPort: String, portMissing: String }

  connect() {
    if (!this.#bridge) {
      this.unavailableTarget.hidden = false
      this.fieldTargets.forEach((field) => (field.disabled = true))
      return
    }
    this.#load()
  }

  async save() {
    if (!this.#bridge) return
    const patch = Object.fromEntries(this.fieldTargets.map((field) => [field.dataset.setting, field.value.trim()]))
    try {
      this.#render(await this.#bridge.settings.update(patch))
      this.#status(this.savedValue)
    } catch (error) {
      this.#status(`${this.saveFailedValue} ${error.message}`)
    }
  }

  // data-action="config-tool--settings#browse" data-config-tool--settings-setting-param="stLinkUtilityPath"
  async browse({ params: { setting } }) {
    if (!this.#bridge) return
    const field = this.#field(setting)
    const folder = await this.#bridge.settings.pickFolder({
      title: field.labels[0]?.textContent.trim(),
      defaultPath: field.value,
    })
    if (folder === null) return // abgebrochen
    field.value = folder
    await this.save()
  }

  async reloadPorts() {
    if (!this.#bridge || !this.hasPortTarget) return
    try {
      await this.#fillPorts(this.portTarget.value)
    } catch (error) {
      this.#status(`${this.loadFailedValue} ${error.message}`)
    }
  }

  async #load() {
    try {
      const settings = await this.#bridge.settings.get()
      if (this.hasPortTarget) await this.#fillPorts(settings.serialPort)
      this.#render(settings)
    } catch (error) {
      this.#status(`${this.loadFailedValue} ${error.message}`)
    }
  }

  async #fillPorts(selected) {
    const paths = (await this.#bridge.serial.listPorts()).map((port) => port.path)
    const options = [new Option(this.noPortValue, "")]
    paths.forEach((path) => options.push(new Option(path, path)))
    if (selected && !paths.includes(selected)) options.push(new Option(`${selected} (${this.portMissingValue})`, selected))
    this.portTarget.replaceChildren(...options)
    this.portTarget.value = selected ?? ""
  }

  // Die Shell ist die Quelle: immer zeigen, was sie gespeichert hat, nicht was getippt wurde.
  #render(settings) {
    for (const field of this.fieldTargets) {
      field.value = settings[field.dataset.setting] ?? ""
    }
  }

  #field(setting) {
    const field = this.fieldTargets.find((f) => f.dataset.setting === setting)
    if (!field) throw new Error(`no field for setting "${setting}"`)
    return field
  }

  #status(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }

  get #bridge() {
    return window.configTool
  }
}
